/**
 * Run-of-show — the day's programme (Day Schedule tab, Event Day Timeline).
 *
 * These are `tasks` rows with `task_type='run_of_show'`, living in the same
 * table and API as ordinary planning tasks rather than a separate schedule
 * table. `milestone` is repurposed as the programme "section" (e.g. "Rites",
 * "Dinner") and `owner_team` as the category used for colouring — both are
 * free-text columns already used by planning tasks, just meaning something
 * more specific for this task_type. This module is the one place that
 * reshapes the shared Task cache into the shape the timeline UI wants.
 */
import {
  Task,
  loadTasks,
  refreshTasksFromApi,
  createTask,
  updateTask,
  archiveTask,
  resolveAssigneeNames,
} from './TasksService';
import { ScheduleCategory, normalizeScheduleCategory } from '../constants/scheduleCategories';

export interface RunOfShowItem {
  id: string;
  eventId: string;        // first tagged event — the one this item is filed/edited under
  eventIds: string[];     // an item can span more than one event (e.g. prep spanning two blocks)
  time: string;            // HH:MM (24h)
  endTime?: string;        // HH:MM (24h)
  activity: string;
  notes?: string;
  assignee?: string;
  category: ScheduleCategory;
  section?: string;
  isCustom: boolean;
}

export interface RunOfShowItemInput {
  time: string;
  endTime?: string;
  activity: string;
  category: ScheduleCategory;
  section?: string;
  notes?: string;
  assignee?: string;
}

function isRunOfShow(task: Task): boolean {
  return task.taskType === 'run_of_show' && !task.archived;
}

function toRunOfShowItem(task: Task): RunOfShowItem {
  return {
    id: task.id,
    eventId: task.eventIds?.[0] ?? '',
    eventIds: task.eventIds ?? [],
    time: task.startTime ?? '00:00',
    endTime: task.endTime,
    activity: task.task,
    notes: task.notes,
    assignee: task.assignees.length > 0 ? resolveAssigneeNames(task.assignees) : undefined,
    category: normalizeScheduleCategory(task.ownerTeam),
    section: task.milestone || undefined,
    isCustom: task.isCustom,
  };
}

/** Programme items tagged to a specific event, from the in-memory cache. */
export function loadRunOfShow(eventId: string): RunOfShowItem[] {
  return loadTasks()
    .filter(t => isRunOfShow(t) && (t.eventIds ?? []).includes(eventId))
    .map(toRunOfShowItem);
}

/** Every programme item across every event, from the in-memory cache. */
export function loadAllRunOfShow(): RunOfShowItem[] {
  return loadTasks().filter(isRunOfShow).map(toRunOfShowItem);
}

export async function refreshRunOfShowFromApi(eventId: string): Promise<RunOfShowItem[]> {
  await refreshTasksFromApi();
  return loadRunOfShow(eventId);
}

export async function refreshAllRunOfShowFromApi(): Promise<RunOfShowItem[]> {
  await refreshTasksFromApi();
  return loadAllRunOfShow();
}

export async function createRunOfShowItem(eventId: string, data: RunOfShowItemInput): Promise<RunOfShowItem> {
  const task = await createTask({
    task: data.activity,
    priority: 'P1',
    ownerTeam: data.category,
    assignees: data.assignee ? [data.assignee] : [],
    status: 'Not Started',
    startTime: data.time,
    endTime: data.endTime,
    milestone: data.section,
    notes: data.notes,
    eventIds: [eventId],
    taskType: 'run_of_show',
  });
  return toRunOfShowItem(task);
}

export async function updateRunOfShowItem(id: string, data: RunOfShowItemInput): Promise<RunOfShowItem | null> {
  const task = await updateTask(id, {
    task: data.activity,
    ownerTeam: data.category,
    assignees: data.assignee ? [data.assignee] : [],
    startTime: data.time,
    endTime: data.endTime,
    milestone: data.section,
    notes: data.notes,
  });
  return task ? toRunOfShowItem(task) : null;
}

/** Soft-delete — archives the underlying task so it's recoverable from Trash. */
export async function removeRunOfShowItem(id: string): Promise<boolean> {
  const task = await archiveTask(id);
  return task !== null;
}
